import React, { useEffect, useState } from "react";
import {
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  Button,
  View,
  Flex,
  Label,
  Input,
  CheckboxField,
  Text,
} from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { get, post } from 'aws-amplify/api';
import awsExports from "src/aws-exports";
import { FiPlus } from "react-icons/fi";
import { Cache } from 'aws-amplify/utils';
import { FiCopy,  } from 'react-icons/fi';  // Import the copy icon
import { LuMousePointerClick } from "react-icons/lu";

const EmailAccounts = () => {
  const ADDRESSES_CACHE_KEY = 'addressesCache';
  const cacheExpirationDuration = 5 * 60 * 1000; // 5 minutes
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<any[]>([])
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [newAddress, setNewAddress] = useState("");
  const [summarizeEmails, setSummarizeEmails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);

  const handleAccountClick = (emailAddress) => {
    navigate(`/email-accounts/${encodeURIComponent(emailAddress)}`);
  };

  const handleCopyEmail = (event, emailAddress) => {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard.writeText(emailAddress)
      .then(() => {
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  async function getAddresses(useCache = false) {
    setLoading(true);
    try {
      // Check cache first
      if (useCache) {
        const cachedData = await Cache.getItem(ADDRESSES_CACHE_KEY);
        if (cachedData) {
          setAddresses(cachedData);
          setLoading(false);
          return;
        }
      }

      // Fetch data from API if not cached or cache is bypassed
      const restOperation = get({
        apiName: 'disposible',
        path: 'addresses',
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      if (Array.isArray(response)) {
        setAddresses(response);
        // Cache the data
        Cache.setItem(ADDRESSES_CACHE_KEY, response, { expires: new Date().getTime() + cacheExpirationDuration }); // Expires in 1800 seconds (30 minutes)
      }
    } catch (err) {
      console.error('GET call failed: ', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  // Amplify wraps a non-2xx reply in an ApiError whose response.body is the
  // raw text. Our API returns a JSON string for 4xx and {"error": ...} for 5xx.
  const describeApiError = (err: unknown): string => {
    const body = (err as { response?: { body?: string } })?.response?.body;
    if (typeof body === 'string' && body) {
      try {
        const parsed = JSON.parse(body);
        if (typeof parsed === 'string') return parsed;
        if (parsed && typeof parsed.error === 'string') return parsed.error;
      } catch {
        return body;
      }
    }
    return 'Could not create the address';
  };

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitStatus(null);
    // Mirror the server's normalization (trim + lowercase) so the row we add
    // locally is the one the list will return later.
    const created = `${newAddress}@${awsExports.emailDomain}`.trim().toLowerCase();
    try {
      const postData = {
        new_address: created,
        summarize_emails: summarizeEmails,
      };
      // post() returns an operation object; the request is its .response
      // promise. Awaiting the object resolved immediately, so a rejected
      // request still showed up as a created address.
      const { response } = post({ apiName: 'disposible', path: 'addresses', options: { body: postData } });
      await response;
      // The list endpoint reads the UserIndex GSI, which is eventually
      // consistent, so refetching right now can miss the row we just created
      // and make a successful create look like a no-op. Add it locally.
      setAddresses(prev => (prev.some(item => item.address === created) ? prev : [...prev, { address: created }]));
      setSubmitStatus(`Created ${created}`);
      setNewAddress("");
      setSummarizeEmails(false);
    } catch (err) {
      console.error('POST call failed: ', err);
      setSubmitError(describeApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    getAddresses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error fetching addresses</div>;

  return (
    <>
      <Flex className="header-flex" justifyContent="flex-start">
        <div className="email-header">
          <h2>Email Accounts</h2>
        </div>
      </Flex>
      <Flex direction="row" alignItems="center" gap="small" justifyContent="flex-end" className="form-flex">
        <Label htmlFor="new_address" className="form-label">New Address:</Label>
        <Input id="new_address" name="new_address" className="form-input" placeholder={`@${awsExports.emailDomain}`} value={newAddress} onChange={(e) => setNewAddress(e.target.value)} />
        <CheckboxField isDisabled={submitting} label="Summarize Emails" name="summarize_emails" checked={summarizeEmails} onChange={(e) => setSummarizeEmails(e.target.checked)} />
        <Button isLoading={submitting} isDisabled={submitting || !newAddress.trim()} variation="primary" className="form-button" aria-label="Add address" onClick={handleSubmit}>
          <FiPlus />
        </Button>
      </Flex>
      {(submitError || submitStatus) && (
        <Flex justifyContent="flex-end" className="form-flex">
          {submitError
            ? <Text role="alert" variation="error">{submitError}</Text>
            : <Text role="status">{submitStatus}</Text>}
        </Flex>
      )}
      <Flex justifyContent="normal"
          alignItems="center"
          alignContent="flex-start">
          <View as="div"
              backgroundColor="var(--amplify-colors-white)"
              boxShadow="3px 3px 5px 6px var(--amplify-colors-neutral-60)"
              borderRadius="6px"
              marginTop="1rem"
              width="100%"
          >
              <Table highlightOnHover={true} className="responsive-table">
                  <TableHead>
                      <TableRow>
                          <TableCell as="th">Address</TableCell>
                          <TableCell as="th" style={{ textAlign: "center" }}>Actions</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {addresses.map((item, index) => {
                          return (
                              <TableRow onClick={() => handleAccountClick(item.address)} key={index}>
                                  <TableCell data-label="Address">{item.address}</TableCell>
                                  <TableCell data-label="Actions">
                                      <Flex justifyContent="center" gap="small">
                                          <Button onClick={(event) => handleCopyEmail(event, item.address)} size="small">
                                              <FiCopy />&nbsp;Copy Address
                                          </Button>
                                          <Button onClick={() => handleAccountClick(item.address)} size="small">
                                              <LuMousePointerClick/>&nbsp;Select
                                          </Button>
                                      </Flex>
                                  </TableCell>
                              </TableRow>
                          );
                      })}
                  </TableBody>
              </Table>
          </View>
      </Flex>
    </>
  );
};

export default EmailAccounts;
